import { SECURE_BROWSER_COMMAND_STATUS } from '../components/SecureBrowser/util';
import { EVALUATION_STATE } from '../constants/Constants';

export default {
  auth: {
    initUserDetails: {},
    appHasError: false,
    appError: null,
  },
  assessment: {
    assessmentType: null,
    assessmentId: null,
    envSetup: { isReady: null, totalRetries: 0 },
    djpHealthStatus: {},
  },
  test: {
    feedback: null,
    isTestSubmitted: false,
    isTestSubmitInProcess: false,
    testEndTime: null,
    cacheProblemData: false,
    isUploadInProgress: {},
    isAudioUploadInProgress: {},
    isVideoUploadFailed: {},
    isTestSubmitting: false,
    currentlyPlayingAudioSlug: null,
    isSecureBrowserEnable: false,
    audioPlayCountObj: {},
  },
  problems: {
    cached: [],
    sectionsGrouped: [],
    indexedProblems: {},
    slugChangeFromRouter: false,
  },
  solutions: {},
  revision: {},
  testSolutionSet: {},
  sections: {
    forSolutionPage: [],
    sectionsNotGrouped: [],
  },
  progress: {
    enable: false,
    message: null,
  },
  appStatus: {
    serverTime: null,
    offsetTime: 0,
    isSystemTimeMismatch: false,
    intercomIds: {},
    bookmarks: {},
    attemptedProblems: null,
    showSystemCheck: false,
    deviceCheck: { kinds: [], reason: null, openedByMonitor: false },
    deviceRefreshCount: 0,
    reportIssueData: null,
    toast: { type: null, show: false, message: '', apiErrorInfo: null },
    isSingleProblemView: false,
    saveInProgress: {},
    openIframeInModal: { success: null, url: null },
    isOffline: false,
    showPreparingAssessmentModal: false,
    showAutoSubmitModal: false,
    autoSubmitReason: null,
    isTimerExpired: false,
  },
  coding: {
    /**
     * - We store the initial code (stub, from API or from localstorage)
     * for both not solved and attempted problems in the solution object
     * - When we switching the language for both attempted and
     * unsolved problems we store it in solution object
     */
    solution: {},
    /**
     * each object in problemData has the following structure
     * probSlug: {
     *  allowedTechnologies,
     *  currentStub,
     *  code, <------ any change in the code will be stored here
     *  codeLockLength,
     *  selectedStub
     *  enableStubLock,
     * }
     * the current selected language is
     * the only dynamic value that gets stored in
     * the solution
     */
    problemData: {},
    theme: null,
    stubs: {},
    defaultLanguage: '',
    intellisenseEnabled: null,
    /**
     * Stores the result that comes from the socket for run code with custom input
     * it has the following structure
     * problem_slug: {
     *  apiResponse: { // has the response from run code api
     *   sid: String, // used to uniquely identify the run-code result for a problem
     *  },
     *  ... other data
     * }
     */
    customInputResult: {},
    testCasesResult: {},
    isLibraryDownloaded: false,
    technologies: {
      technologiesMap: {},
      technologiesMapByUri: {},
      technologies: {},
    },
  },
  proctor: {
    // Holds one of these exception https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
    webcamException: null,
    webcamEnabled: false,
    webcamPermission: false,
    audioPermission: false,
    audioPermissionLoading: false,
    webcamPermissionLoading: false,
    snapshotInterval: null,
    hasIdentityImage: null,
    isIdentityImageUploaded: null,
    isIdentityImageVerifying: null,
    identityImageUrl: null,
    idProofImageUrl: null,
    hasIdProofImage: null,
    isSampleAudioUploadInProgress: false,
    hasAudioSample: false,
    audioSampleUrl: null,
    isIdProofImageUploaded: null,
    isIdProofImageVerifying: null,
    fullScreenEnabled: false,
    visibilityEnabled: false,
    showBrowserTolerance: false,
    browserToleranceCount: null,
    identityImageErrMessage: null,
    isProctoringABEnabled: false,
    isProctorBatchABEnabled: false,
    proctoringSignedUrls: {},
    fetchingSignedUrls: false,
    signedUrlBatchSize: 0,
    isRealtimeFeedbackEnabled: false,
    nextProctorImageSequence: 1,
    proctorSessionId: null,
    proctorTabId: null,
    multiSession: false,
    secureBrowserMeta: {
      systemCheck: {
        isNotificationEnable: SECURE_BROWSER_COMMAND_STATUS.DEFAULT,
        isRestrictedAppsRunning: SECURE_BROWSER_COMMAND_STATUS.DEFAULT,
        multiMonitors: SECURE_BROWSER_COMMAND_STATUS.DEFAULT,
        permissionTocloseApp: false,
        checksPassed: false,
      },
      reverify: false,
    },
    vadBlockingProblemType: null,
    webcamRenderKey: 0, // Used to force re-render webcam component when webcam error occurs and user tries to re-enable webcam
  },
  socket: {
    socket: null,
    canWebsocketConnectionInitiated: null,
    isSocketFetchedSettings: null,
    socketActive: false,
    connid: null,
    wid: null,
    sockid: null,
    fingerPrint: null,
    trackVisibilityFocus: 0,
    trackVisibilityBlur: 0,
    captureScreenEvent: 0,
    routeChange: null,
    showMultiSession: false,
    initSessionMonitorData: null,
    failedSocketEventsStatus: false,
    isSessionActive: false,
    isSessionCreated: false,
    sessionId: null,
    shouldDisconnect: false,
    // Why do we have both `socketActive` and `socketActiveV2`?
    // The existing `socketActive` variable has multiple dependencies and is not functioning as intended. Its value always remains `true` due to the lack of           proper handling for socket disconnection events (`socketDisconnect`) in the current implementation.
    // To avoid breaking existing functionality, we introduced `socketActiveV2` as a more reliable alternative.
    // Moving forward, we plan to deprecate `socketActive` as part of the refactoring process.

    socketActiveV2: false,
  },
  onboarding: {
    uploadedFiles: {},
  },
  report: {
    reportSolutionSet: {},
    reportSolutions: {},
    showCodeQualityAnnotation: false,
  },
  desmond: {
    solution: {},
    problemData: {},
    testCasesResult: {},
    customInputResult: {},
    runProject: {},
  },
  infra: {
    isReady: false,
  },
  podBased: {
    testProjectStates: {},
    testProject: {
      evaluationState: EVALUATION_STATE.IDLE,
      isLogContentLengthAvailable: false,
    },
  },
  whitelabelCompanyData: {
    isCallCompleted: false,
  },
  aiInterview: {
    validateDhwaniInterviewSession: {
      loading: false,
      data: null,
      error: null,
    },
    interviewSession: null,
  },
};
